<template>
  <DropoffReportDataIntegrityFullScreenMessage v-if="flags.hideDropOff" />
  <div v-else-if="!isEmptyData || pageLoading" class="dropoff-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    >
      <template #header:bottom>
        <div
          :class="{ 'split-banner': showTwoBanners }"
        >
          <InsightMessageBanner
            v-if="showDataIntegrityWarningBanner"
            :header="dataIntegrityMessage.header"
            :message="dataIntegrityMessage.message"
            :beacon-article-id="dataIntegrityMessage.beaconArticleId"
            :data-png-remove="dataIntegrityMessage.dataPngRemove"
            :warning-red="true"
            @close="showDataIntegrityWarningBanner = false;"
          />
          <InsightMessageBanner
            :header="reportingMessage.header"
            :message="reportingMessage.message"
            :beacon-article-id="reportingMessage.beaconArticleId"
            :data-png-remove="reportingMessage.dataPngRemove"
            @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
          />
        </div>
      </template>
    </InsightHeader>
    <div class="dropoff-report-content">
      <DropoffReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="data"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
      />
      <DropoffReportCandidatesWidget
        class="dropoff-candidates"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <DropoffReportMostDropoffsWidget
        class="dropoff-most-dropoffs"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <DropoffReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :table-rows="dropoffTableRows"
        :table-totals="dropoffTableTotals"
        :downloading="downloading"
        @download-csv="downloadDropoffTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, ref, watch,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { usePassthroughDropoffReport } from '@/stores/insights/passthroughDropoffReport';
import DropoffReportCandidatesWidget from '@/components/insights/ProcessInsights/DropoffReport/DropoffReportCandidatesWidget.vue';
import DropoffReportMostDropoffsWidget from '@/components/insights/ProcessInsights/DropoffReport/DropoffReportMostDropoffsWidget.vue';
import DropoffReportTable from '@/components/insights/ProcessInsights/DropoffReport/DropoffReportTable.vue';
import DropoffReportSummary from '@/components/insights/ProcessInsights/DropoffReport/DropoffReportSummary.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsights } from '@/stores/insights/insights';
import { useTooltips } from '@/stores/insights/tooltips';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import InsightMessageBanner from '@/components/insights/InsightMessageBanner.vue';
import { dropoffDataIntegrityWarning } from '@/helpers/constants/reportingWarnings';
import DropoffReportDataIntegrityFullScreenMessage from '@/components/insights/ProcessInsights/DropoffReport/DropoffReportDataIntegrityFullScreenMessage.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData, data, dropoffTableRows, error, dropoffTableTotals, flags,
} = storeToRefs(usePassthroughDropoffReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadDropoffReport, downloadDropoffCSV } = usePassthroughDropoffReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const projectName = computed(() => authData.value.projectName);

const showDataIntegrityWarningBanner = ref(false);
const dataIntegrityMessage = ref({ header: '', message: '', link: '' });
const downloading = ref(false);

const showTwoBanners = computed(() => flags.value.applyWarning
  && Boolean(reportingMessage.value.message?.length));

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadDropoffReport(params),
]);

const downloadDropoffTable = async () => {
  downloading.value = true;

  await downloadDropoffCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Drop Off' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'process',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      'drop-off-average': 'Metric: Drop-off Rate',
      'drop-off-average-benchmark': 'Metric: Drop-off Benchmark',
      'higher-proportion-drop-offs': 'Graph: Drop-off Rate by Stage (Bar)',
      'most-drop-offs': 'Graph: Drop-off Rate by Stage (Donut)',
      'dropoff-report-table': 'Table: Drop-off Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => downloadDropoffCSV({
          dateRange: dateRange.value,
          projectName: projectName.value,
        }),
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});

watch(() => flags.value.applyWarning, (value) => {
  if (value) {
    showDataIntegrityWarningBanner.value = true;
    dataIntegrityMessage.value = dropoffDataIntegrityWarning;
  } else { // Handles reset on navigation from hiring to dropoff
    showDataIntegrityWarningBanner.value = false;
  }
}, { immediate: true });
</script>

<style lang="less" scoped>
.dropoff-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.dropoff-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "summary summary"
    "dropoffCandidates dropoffMostDropoffs"
    "table table";
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.summary {
  grid-area: summary;
}

.dropoff-candidates {
  grid-area: dropoffCandidates;
}

.dropoff-most-dropoffs {
  grid-area: dropoffMostDropoffs;
}

.table {
  grid-area: table;
}

.split-banner {
  display: flex;
  justify-content: space-between;
}
</style>
